import { useState } from 'react';
import { api as http } from '../lib/api.ts';
import { item, separator, useMenu } from './menu.tsx';
import {
  applyTheme, archivePage, childrenOf, createPage, currentTheme, duplicatePage, openPage,
  restorePage, setState, toggleExpanded, updatePage, useStore,
} from './store.ts';
import type { Page } from './types.ts';

export function Sidebar() {
  const workspace = useStore((s) => s.workspace);
  const me = useStore((s) => s.me);
  const pages = useStore((s) => s.pages);
  const trashCount = useStore((s) => s.trashCount);
  const open = useStore((s) => s.sidebarOpen);
  const [trash, setTrash] = useState<Page[] | null>(null);
  const [menu, openMenu] = useMenu();

  const roots = childrenOf(pages, null);
  const favorites = Object.values(pages).filter((p) => p.favorite && !p.archived);

  const showTrash = async () => {
    if (trash) { setTrash(null); return; }
    try { setTrash(await http.get<Page[]>('/api/trash')); } catch { setTrash([]); }
  };

  if (!open) {
    return (
      <button
        type="button" className="rail-open" aria-label="Show the sidebar"
        onClick={() => setState({ sidebarOpen: true })}
      >»</button>
    );
  }

  return (
    <aside className="side">
      <div className="side-top">
        <button
          type="button" className="side-ws"
          onClick={(e) => openMenu(e, [
            item('New page', () => void createPage({})),
            separator(),
            ...(['light', 'dark', 'system'] as const).map((t) =>
              item(`${t[0]!.toUpperCase()}${t.slice(1)}`, () => applyTheme(t),
                { hint: currentTheme() === t ? '✓' : undefined })),
            separator(),
            item('Sign out', () => void http.post('/api/auth/logout').then(() => location.reload())),
          ])}
        >
          <span className="side-ws-icon" style={{ background: me.color }}>
            {workspace.icon || (workspace.name[0] ?? 'V').toUpperCase()}
          </span>
          <span className="side-ws-name">{workspace.name}</span>
          <span className="side-ws-caret">⌄</span>
        </button>
        <button
          type="button" className="side-collapse" aria-label="Hide the sidebar"
          onClick={() => setState({ sidebarOpen: false })}
        >«</button>
      </div>

      <div className="side-actions">
        <button type="button" className="side-action" onClick={() => setState({ finding: true })}>
          <span aria-hidden>🔍</span> Search
          <kbd>Ctrl K</kbd>
        </button>
        <button type="button" className="side-action" onClick={() => void createPage({})}>
          <span aria-hidden>✏️</span> New page
        </button>
      </div>

      {favorites.length ? (
        <div className="side-section">
          <div className="side-head">Favorites</div>
          {favorites.map((page) => <PageRow key={page.id} page={page} depth={0} favorites />)}
        </div>
      ) : null}

      <div className="side-section grow">
        <div className="side-head">
          Private
          <button type="button" aria-label="New page" onClick={() => void createPage({})}>+</button>
        </div>
        {roots.length
          ? roots.map((page) => <PageRow key={page.id} page={page} depth={0} />)
          : <div className="side-empty">No pages yet.</div>}
      </div>

      <div className="side-bottom">
        <button type="button" className="side-action" onClick={() => void showTrash()}>
          <span aria-hidden>🗑️</span> Trash
          {trashCount ? <span className="side-count">{trashCount}</span> : null}
        </button>
        {trash ? (
          <div className="side-trash">
            {trash.length ? trash.map((page) => (
              <div key={page.id} className="side-trash-row">
                <span>{page.icon || '📄'} {page.title || 'Untitled'}</span>
                <button type="button" onClick={() => void restorePage(page.id).then(() => setTrash(null))}>
                  Restore
                </button>
              </div>
            )) : <div className="side-empty">The trash is empty.</div>}
          </div>
        ) : null}
      </div>
      {menu}
    </aside>
  );
}

function PageRow({ page, depth, favorites }: { page: Page; depth: number; favorites?: boolean }) {
  const pages = useStore((s) => s.pages);
  const openId = useStore((s) => s.openId);
  const expanded = useStore((s) => !!s.expanded[page.id]);
  const [menu, openMenu] = useMenu();

  const kids = childrenOf(pages, page.id);
  const isOpen = openId === page.id;

  return (
    <>
      <div
        className={`side-page${isOpen ? ' on' : ''}`}
        style={{ paddingLeft: 8 + depth * 14 }}
        onDragOver={(e) => { if (e.dataTransfer.types.includes('text/vnote-page')) e.preventDefault(); }}
        onDrop={(e) => {
          const moving = e.dataTransfer.getData('text/vnote-page');
          if (!moving || moving === page.id) return;
          e.preventDefault();
          void updatePage(moving, { parent_id: page.id });
        }}
      >
        <button
          type="button"
          className={`side-twist${kids.length ? '' : ' empty'}${expanded ? ' open' : ''}`}
          aria-label={expanded ? 'Collapse' : 'Expand'}
          onClick={(e) => { e.stopPropagation(); toggleExpanded(page.id); }}
        >▸</button>

        <button
          type="button"
          className="side-link"
          draggable={!favorites}
          onDragStart={(e) => e.dataTransfer.setData('text/vnote-page', page.id)}
          onClick={() => void openPage(page.id)}
        >
          <span className="side-icon">{page.icon || (page.is_database ? '🗂️' : '📄')}</span>
          <span className="side-title">{page.title || 'Untitled'}</span>
        </button>

        <button
          type="button" className="side-more" aria-label="Page options"
          onClick={(e) => openMenu(e, [
            item(page.favorite ? 'Remove from favorites' : 'Add to favorites',
              () => void updatePage(page.id, { favorite: !page.favorite })),
            item('Duplicate', () => void duplicatePage(page.id)),
            item('Rename', () => {
              const title = window.prompt('Page name', page.title);
              if (title !== null) void updatePage(page.id, { title });
            }),
            separator(),
            item('Copy as markdown', () => {
              void http.get<{ markdown: string }>(`/api/pages/${page.id}/markdown`)
                .then((res) => navigator.clipboard?.writeText(res.markdown));
            }),
            separator(),
            item('Move to trash', () => void archivePage(page.id), { danger: true }),
          ])}
        >⋯</button>

        <button
          type="button" className="side-add" aria-label="Add a page inside"
          onClick={() => void createPage({ parentId: page.id })}
        >+</button>
      </div>

      {expanded && kids.map((kid) => <PageRow key={kid.id} page={kid} depth={depth + 1} />)}
      {expanded && !kids.length ? (
        <div className="side-empty" style={{ paddingLeft: 22 + depth * 14 }}>No pages inside</div>
      ) : null}
      {menu}
    </>
  );
}
